import { useApp } from '../context';
import { Empty, Panel, Stat, StateBadge } from '../ui';
import { dateLabel, money, nairaShort } from '../../lib/format';
import { MigrateWorldPanel } from '../MigrateWorld';
import { balanceAsOf, isInvestmentAccount, type Account } from '../../data/types';

const TYPE_LABELS: Record<string, string> = {
  money_market: 'money market · counted in reserve', current: 'current account', savings: 'savings',
  domiciliary: 'dollar account', card: 'card', pension: 'pension', investment: 'investment', property: 'property',
};
const typeLabel = (t: string | null | undefined) => (t ? TYPE_LABELS[t.toLowerCase()] ?? t.replace(/_/g, ' ') : null);

export function Wealth() {
  const { base } = useApp();
  const byCurrency = new Map<string, Account[]>();
  // Cash and investments are shown apart, per currency.
  for (const a of base.accounts) {
    const key = `${a.currency} ${isInvestmentAccount(a) ? 'investments' : 'bank accounts'}`;
    byCurrency.set(key, [...(byCurrency.get(key) ?? []), a]);
  }
  const property = base.positions.filter((p) => p.position_type === 'property');
  const business = base.positions.filter((p) => p.position_type === 'business');

  return (
    <div className="stack">
      <Panel className="period-bar">
        <h2>What you hold and owe <StateBadge state="As of">As recorded</StateBadge></h2>
        <p className="muted">Each figure shows the date it was last recorded. These are not live bank balances.</p>
      </Panel>

      {[...byCurrency.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([group, accounts]) => {
        const currency = accounts[0]!.currency;
        const recorded = accounts.filter((a) => a.current_balance !== null);
        const total = recorded.reduce((s, a) => s + (a.current_balance ?? 0), 0);
        return (
          <Panel key={group} title={group} meta={recorded.length ? `Total ${money(total, currency)}` : 'No balances recorded'}>
            <ul className="acct-list">
              {accounts.map((a) => (
                <li key={a.id}>
                  <div><strong>{a.name}</strong><span>{[a.institution, typeLabel(a.account_type)].filter(Boolean).join(' · ')}</span></div>
                  <div className="acct-amt"><b>{money(a.current_balance, a.currency)}</b><span>as of {dateLabel(balanceAsOf(a))}</span></div>
                </li>
              ))}
            </ul>
          </Panel>
        );
      })}
      {!base.accounts.length && <Panel title="Accounts"><Empty>No accounts recorded.</Empty></Panel>}

      <MigrateWorldPanel />

      <div className="grid-2">
        <Panel title="Property" meta="Capital, not spending">
          {property.length ? property.map((p) => (
            <div className="position" key={p.id}>
              <h4>{p.name}</h4>
              <div className="stats-3">
                <Stat label="Purchase price" value={nairaShort(p.purchase_price)} />
                <Stat label="Paid" value={nairaShort(p.amount_paid)} />
                <Stat label="Outstanding" value={nairaShort(p.outstanding_amount)} />
              </div>
              {p.notes && <p className="muted">{p.notes}</p>}
              <span className="asof">as of {dateLabel(p.updated_at)}</span>
            </div>
          )) : <Empty>No property positions recorded.</Empty>}
        </Panel>
        <Panel title="Business capital" meta="Owner funding, kept apart from lifestyle">
          {business.length ? business.map((p) => (
            <div className="position" key={p.id}>
              <h4>{p.name}</h4>
              <div className="stats-3">
                <Stat label="Owner funding" value={nairaShort(p.owner_funding)} />
                <Stat label="Returned" value={nairaShort(p.amount_returned)} />
                <Stat label="Net out" value={p.owner_funding === null ? '—' : nairaShort((p.owner_funding ?? 0) - (p.amount_returned ?? 0))} />
              </div>
              {p.notes && <p className="muted">{p.notes}</p>}
              <span className="asof">as of {dateLabel(p.updated_at)}</span>
            </div>
          )) : <Empty>No business positions recorded.</Empty>}
        </Panel>
      </div>

      <Panel className="trust">
        <strong>Net worth is deliberately not shown.</strong>
        <p className="muted">It needs valuation dates and liability rules for every asset first. A number without those would look precise and be wrong.</p>
      </Panel>
    </div>
  );
}
